import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCourses } from '../data/course-data';
import {
    recommendCourses,
    buildLeadPayload,
    getBudgetOptions,
    isValidPhone,
} from '../utils/zurielRecommendations';
import './AIAgent.css';

const INTERESTS = [
    'Web Development', 'Design', 'Data Analysis', 'Marketing',
    'Programming', 'Security', 'AI', 'Blockchain',
];

const AIAgent = ({ onClose }) => {
    const navigate = useNavigate();
    const modalRef = useRef(null);
    const [step, setStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [aiMessage, setAiMessage] = useState('');
    const [isTyping, setIsTyping] = useState(true);
    const [usedFallback, setUsedFallback] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        experience: '',
        interests: [],
        goals: '',
        timeCommitment: '',
        budget: '',
    });
    const [recommendedCourses, setRecommendedCourses] = useState([]);

    const courses = getCourses();
    const budgetOptions = getBudgetOptions(courses);

    useEffect(() => {
        if (step !== 1) return;

        setAiMessage('');
        setIsTyping(true);
        const message = "Hi! I'm Zuriel, your Course Advisor. Answer a few questions and I'll match you with the best courses for your goals.";
        let i = 0;
        const typing = setInterval(() => {
            if (i < message.length) {
                setAiMessage(prev => prev + message[i]);
                i++;
            } else {
                clearInterval(typing);
                setIsTyping(false);
            }
        }, 35);
        return () => clearInterval(typing);
    }, [step]);

    useEffect(() => {
        modalRef.current?.focus();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleInterestChange = (interest) => {
        setFormData(prev => ({
            ...prev,
            interests: prev.interests.includes(interest)
                ? prev.interests.filter(i => i !== interest)
                : [...prev.interests, interest],
        }));
    };

    const handleStep1Submit = (e) => {
        e.preventDefault();
        if (!isValidPhone(formData.phone)) {
            setSubmitError('Please enter a valid phone number (at least 10 digits).');
            return;
        }
        setSubmitError('');
        setStep(2);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.interests.length === 0) {
            setSubmitError('Please select at least one area of interest.');
            return;
        }

        setSubmitError('');
        setIsLoading(true);

        const recommendations = recommendCourses(courses, formData);
        const hasStrongMatch = recommendations.some(c => !c.isFallback && c.matchScore > 0);
        setUsedFallback(!hasStrongMatch);

        const registrationData = buildLeadPayload(formData, recommendations[0]);

        try {
            const response = await fetch('https://server.abiatechhub.com/register', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(registrationData),
            });
            await response.json();
        } catch {
            // Still show recommendations even if lead capture fails
        } finally {
            setRecommendedCourses(recommendations);
            setStep(3);
            setIsLoading(false);
        }
    };

    const handleCourseSelect = (courseSlug) => {
        localStorage.setItem('prospectData', JSON.stringify({
            ...formData,
            recommendedCourses: recommendedCourses.map(c => c.slug),
        }));
        navigate(`/courses/${courseSlug}`);
        onClose?.();
    };

    const handleEnroll = (courseName) => {
        localStorage.setItem('prospectData', JSON.stringify(formData));
        navigate(`/register?course=${encodeURIComponent(courseName)}`);
        onClose?.();
    };

    const handleClose = () => onClose?.();

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') handleClose();
    };

    const progress = (step / 3) * 100;

    return (
        <div className="ai-agent-popup" onKeyDown={handleKeyDown}>
            <div className="ai-agent-content" ref={modalRef} tabIndex={-1} role="dialog" aria-labelledby="zuriel-title" aria-modal="true">
                <button className="close-btn" onClick={handleClose} aria-label="Close">
                    <i className="fas fa-times"></i>
                </button>

                <div className="ai-header">
                    <div className="ai-avatar">
                        <i className="fas fa-robot"></i>
                    </div>
                    <div>
                        <h2 id="zuriel-title">Zuriel</h2>
                        <p className="ai-subtitle">Course Advisor</p>
                    </div>
                </div>

                <div className="zuriel-progress" aria-hidden="true">
                    <div className="zuriel-progress-fill" style={{ width: `${progress}%` }}></div>
                </div>
                <p className="step-indicator">Step {step} of 3</p>

                {submitError && (
                    <div className="ai-error" role="alert">{submitError}</div>
                )}

                {isLoading ? (
                    <div className="loading">
                        <div className="loading-spinner"></div>
                        <p>Matching you with the best courses...</p>
                    </div>
                ) : (
                    <>
                        {step === 1 && (
                            <div className="form-step">
                                <div className="ai-message">
                                    <p>{aiMessage}{isTyping && <span className="typing-cursor">|</span>}</p>
                                </div>
                                <form onSubmit={handleStep1Submit}>
                                    <h3>Let's get to know you</h3>
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            name="name"
                                            placeholder="Your full name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <input
                                            type="email"
                                            name="email"
                                            placeholder="Your email address"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <input
                                            type="tel"
                                            name="phone"
                                            placeholder="Your phone number"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <button type="submit" className="btn btn-ath-purple">
                                        Continue <i className="fas fa-arrow-right"></i>
                                    </button>
                                </form>
                            </div>
                        )}

                        {step === 2 && (
                            <div className="form-step">
                                <div className="ai-message">
                                    <p>Great! Now tell me about your learning preferences so I can find your best matches.</p>
                                </div>
                                <form onSubmit={handleSubmit}>
                                    <div className="form-group">
                                        <label>What's your current experience level?</label>
                                        <select name="experience" value={formData.experience} onChange={handleInputChange} required>
                                            <option value="">Select your experience</option>
                                            <option value="beginner">Beginner — new to tech</option>
                                            <option value="intermediate">Intermediate — some experience</option>
                                            <option value="advanced">Advanced — ready for intensive courses</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>What areas interest you? (Select at least one)</label>
                                        <div className="interests-grid">
                                            {INTERESTS.map(interest => (
                                                <label key={interest} className={`interest-checkbox ${formData.interests.includes(interest) ? 'selected' : ''}`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.interests.includes(interest)}
                                                        onChange={() => handleInterestChange(interest)}
                                                    />
                                                    {interest}
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>What are your career goals?</label>
                                        <textarea
                                            name="goals"
                                            value={formData.goals}
                                            onChange={handleInputChange}
                                            placeholder="e.g. I want to become a web developer and build apps for businesses"
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>How much time can you commit per week?</label>
                                        <select name="timeCommitment" value={formData.timeCommitment} onChange={handleInputChange} required>
                                            <option value="">Select time commitment</option>
                                            <option value="5-10">5–10 hours</option>
                                            <option value="10-20">10–20 hours</option>
                                            <option value="20+">20+ hours</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>What's your budget range?</label>
                                        <select name="budget" value={formData.budget} onChange={handleInputChange} required>
                                            <option value="">Select budget range</option>
                                            {budgetOptions.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-actions">
                                        <button type="button" className="btn btn-ath-outline" onClick={() => setStep(1)}>
                                            <i className="fas fa-arrow-left"></i> Back
                                        </button>
                                        <button type="submit" className="btn btn-ath-purple">
                                            Get Recommendations <i className="fas fa-magic"></i>
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {step === 3 && (
                            <div className="form-step">
                                <div className="ai-message">
                                    <p>
                                        {usedFallback
                                            ? "I couldn't find an exact match, but here are courses that fit your profile and budget:"
                                            : "Based on your goals, experience, and budget — here are your top matches:"}
                                    </p>
                                </div>

                                {recommendedCourses.length === 0 ? (
                                    <div className="empty-recommendations">
                                        <p>No courses found right now. Browse our full catalog or speak with an advisor.</p>
                                        <div className="form-actions">
                                            <button onClick={() => { navigate('/courses'); onClose?.(); }} className="btn btn-ath-purple">
                                                Browse All Courses
                                            </button>
                                            <button onClick={() => { navigate('/contact'); onClose?.(); }} className="btn btn-ath-outline">
                                                Contact Us
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="recommended-courses">
                                            {recommendedCourses.map((course, index) => (
                                                <div key={course.slug} className="recommended-course">
                                                    {index === 0 && !usedFallback && (
                                                        <span className="match-badge">Best Match</span>
                                                    )}
                                                    <img src={course.img} alt={course.alt} />
                                                    <h4>{course.name}</h4>
                                                    <div className="course-meta">
                                                        <span><i className="fas fa-clock"></i> {course.duration}</span>
                                                        <span><i className="fas fa-tag"></i> {course.fee}</span>
                                                    </div>
                                                    <p className="match-reason">{course.matchReason}</p>
                                                    <p className="course-desc">{course.description.slice(0, 120)}...</p>
                                                    <div className="course-actions">
                                                        <button
                                                            onClick={() => handleCourseSelect(course.slug)}
                                                            className="btn btn-ath-purple"
                                                        >
                                                            View Course
                                                        </button>
                                                        <button
                                                            onClick={() => handleEnroll(course.name)}
                                                            className="btn btn-ath-outline"
                                                        >
                                                            Enroll Now
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="form-actions results-footer">
                                            <button type="button" className="btn btn-ath-outline" onClick={() => setStep(2)}>
                                                <i className="fas fa-edit"></i> Edit answers
                                            </button>
                                            <button type="button" className="btn btn-ath-outline" onClick={() => { navigate('/courses'); onClose?.(); }}>
                                                See all courses
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default AIAgent;
